import useSWR from 'swr';

import { getApiClient } from '@apps/web/lib/api_client';
import { useMode } from '@apps/web/lib/mode_store';
import {
    type ApiError,
    type ContactActivityItemRead,
    type ContactActivityResponse,
} from '@libraries/typescript/api_client';

export type ContactActivityItem = ContactActivityItemRead;
export type ContactActivityItemType = ContactActivityItemRead['type'];
export { type ContactActivityResponse };

const CONTACT_ACTIVITY_PATH =
    '/organizations/{organization_id}/conversations/{conversation_id}/contact-activity';

/**
 * Fetches one page of a contact's cross-conversation activity timeline.
 *
 * Pagination is manual: pass the previous page's `next_cursor` back in as
 * `cursor` to load the next page; the panel accumulates items across pages.
 */
export function useContactActivity(
    organizationId: string | null,
    conversationId: string | null,
    cursor: string | null,
    enabled: boolean,
) {
    const apiClient = getApiClient();
    const mode = useMode();
    const shouldFetch = enabled && !!organizationId && !!conversationId;

    return useSWR<ContactActivityResponse, ApiError>(
        shouldFetch
            ? ([
                  'contact-activity',
                  organizationId,
                  conversationId,
                  cursor ?? '',
                  mode,
              ] as const)
            : null,
        async () =>
            apiClient.get({
                url: CONTACT_ACTIVITY_PATH,
                pathParams: {
                    organization_id: organizationId as string,
                    conversation_id: conversationId as string,
                },
                queryParams: cursor ? { cursor } : {},
                mode,
                reason: 'contact-activity-panel',
            }),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
        },
    );
}
